import { applyTransformations, Transformation } from 'main/util/uploadcare'
import * as React from 'react'
import SrcSmartImage from './SrcSmartImage'

const DEFAULT_BASE_TRANSFORMATIONS = [{ format: 'auto' }]
const RETINA_TRANSFORMATIONS = [{ quality: 'lightest' }]
const PLACEHOLDER_BLUR = 100

interface IProps {
  src: string
  height?: number
  width?: number
  critical: boolean
  overflow: boolean
  baseTransformations: Transformation[]
}

export default function UploadcareSmartImage(props: IProps) {
  const { src: url, width, height, baseTransformations } = props
  const srcData = getSrcData(url, baseTransformations, width, height)

  return <SrcSmartImage {...props} {...srcData} />
}

function getSrcData(
  url: string,
  baseTransformations: Transformation[],
  width?: number,
  height?: number,
) {
  if (width !== undefined && height !== undefined) {
    return dimensionsKnown(url, baseTransformations, width, height)
  }
  if (width !== undefined) return widthKnown(url, baseTransformations, width)
  if (height !== undefined) return heightKnown(url, baseTransformations, height)

  return dimensionsUnknown(url, baseTransformations)
}

function dimensionsKnown(
  url: string,
  baseTransformations: Transformation[],
  width: number,
  height: number,
) {
  const normalTransformations = [...baseTransformations, { scaleCrop: `${width}x${height}` }]
  const src = applyTransformations(url, normalTransformations)

  const retinaTransformations = [
    ...baseTransformations,
    ...RETINA_TRANSFORMATIONS,
    { scaleCrop: `${width * 2}x${height * 2}` },
  ]
  const retinaSrc = applyTransformations(url, retinaTransformations)

  const placeholderTransformations = [...normalTransformations, { blur: PLACEHOLDER_BLUR }]
  const placeholderSrc = applyTransformations(url, placeholderTransformations)

  return { src, placeholderSrc, srcSet: `${retinaSrc} 2x` }
}

function widthKnown(url: string, baseTransformations: Transformation[], width: number) {
  const normalTransformations = [...baseTransformations, { resize: `${width}x` }]
  const src = applyTransformations(url, normalTransformations)

  const retinaTransformations = [
    ...baseTransformations,
    ...RETINA_TRANSFORMATIONS,
    { resize: `${width * 2}x` },
  ]
  const retinaSrc = applyTransformations(url, retinaTransformations)

  const placeholderTransformations = [...normalTransformations, { blur: PLACEHOLDER_BLUR }]
  const placeholderSrc = applyTransformations(url, placeholderTransformations)

  return { src, placeholderSrc, srcSet: `${retinaSrc} 2x` }
}

function heightKnown(url: string, baseTransformations: Transformation[], height: number) {
  const normalTransformations = [...baseTransformations, { resize: `x${height}` }]
  const src = applyTransformations(url, normalTransformations)

  const retinaTransformations = [
    ...baseTransformations,
    ...RETINA_TRANSFORMATIONS,
    { resize: `x${height * 2}` },
  ]
  const retinaSrc = applyTransformations(url, retinaTransformations)

  const placeholderTransformations = [...normalTransformations, { blur: PLACEHOLDER_BLUR }]
  const placeholderSrc = applyTransformations(url, placeholderTransformations)

  return { src, placeholderSrc, srcSet: `${retinaSrc} 2x` }
}

function dimensionsUnknown(url: string, baseTransformations: Transformation[]) {
  const normalTransformations = [...baseTransformations, ...RETINA_TRANSFORMATIONS]
  const src = applyTransformations(url, normalTransformations)

  const placeholderTransformations = [...normalTransformations, { blur: PLACEHOLDER_BLUR }]
  const placeholderSrc = applyTransformations(url, placeholderTransformations)

  return { src, placeholderSrc, srcSet: '' }
}

UploadcareSmartImage.defaultProps = {
  baseTransformations: DEFAULT_BASE_TRANSFORMATIONS,
}
